import { useEffect, useState } from "react";
import { Printer, Eye, FileText, Receipt, Contact2, FileCheck2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/primitives";
import { stayDocuments, docUrl, openPdf, fmtINR, BILL_STATUS_TONE } from "@/lib/documents";
import { BTN_SECONDARY, BTN_SM } from "@/components/checkout/buttonStyles";
import { DownloadPdfButton } from "./DownloadPdfButton";
import { requestRegCard } from "./RegCardOptions";
import api from "@/lib/api";

const META = {
  registration_card: { label: "Registration Card", icon: Contact2 },
  receipt: { label: "Payment Receipt", icon: Receipt },
  provisional_bill: { label: "Provisional Bill", icon: FileText },
  final_bill: { label: "Final Bill / Invoice", icon: FileCheck2 },
  invoice: { label: "Consolidated Invoice", icon: FileCheck2 },
};
const labelFor = (d) => (d.type === "invoice" && d.is_gst_invoice ? "GST Tax Invoice" : (META[d.type] || { label: d.type }).label);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

/** Stay → Documents: every printed guest document with Print (PDF) and View (HTML preview). */
export function StayDocumentsList({ stayId, refreshKey }) {
  const [docs, setDocs] = useState(null);
  const [preview, setPreview] = useState(null);
  const [html, setHtml] = useState("");

  useEffect(() => { stayDocuments(stayId).then(setDocs).catch(() => setDocs([])); }, [stayId, refreshKey]);

  const view = async (d) => {
    setPreview(d); setHtml("");
    try { const r = await api.get(`/documents/${d.type}/${d.id}/html`, { responseType: "text" }); setHtml(r.data); } catch { setHtml("<p style='padding:20px;font-family:sans-serif'>Preview unavailable</p>"); }
  };

  if (docs === null) return <div className="text-[12px] text-hocp-textMuted flex items-center gap-2"><Loader2 size={13} className="animate-spin" />Loading documents…</div>;
  return (
    <div className="space-y-2" data-testid="stay-documents-list">
      {docs.length === 0 && <div className="text-[12px] text-hocp-textMuted">No documents issued yet.</div>}
      {docs.map((d) => {
        const m = { ...(META[d.type] || { label: d.type, icon: FileText }), label: labelFor(d) };
        const Icon = m.icon;
        return (
          <div key={`${d.type}-${d.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-hocp-border bg-hocp-bgCard px-3 py-2.5" data-testid={`stay-doc-${d.type}`}>
            <div className="flex items-center gap-3 min-w-0">
              <Icon size={18} strokeWidth={1.75} className="text-[#014751] shrink-0" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-[#1A2425]">{m.label}<span className="mono text-[11.5px] font-normal text-hocp-textMuted">{d.number}</span>
                  {d.is_gst_invoice && <Badge tone="info">GST</Badge>}<Badge tone={BILL_STATUS_TONE[d.status] || "neutral"}>{(d.status || "").replace(/_/g, " ")}</Badge></div>
                <div className="text-[11.5px] text-hocp-textMuted">{fmt(d.date)}{d.amount != null ? ` · ${fmtINR(d.amount)}` : ""}{d.period_from && d.period_to ? ` · ${d.period_from} → ${d.period_to}` : ""}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button type="button" className={`${BTN_SECONDARY} ${BTN_SM}`} onClick={() => view(d)} data-testid={`stay-doc-view-${d.type}`}><Eye size={13} />View</button>
              <button type="button" className={`${BTN_SECONDARY} ${BTN_SM}`} onClick={() => (d.type === "registration_card" ? requestRegCard({ url: docUrl(d.type, d.id), filename: `RegistrationCard-${d.number}.pdf` }) : openPdf(docUrl(d.type, d.id), `${m.label.replace(/[^A-Za-z0-9]/g, "")}-${d.number}.pdf`))} data-testid={`stay-doc-print-${d.type}`}><Printer size={13} />Print</button>
              <DownloadPdfButton url={docUrl(d.type, d.id)} filename={`${m.label.replace(/[^A-Za-z0-9]/g, "")}-${d.number}.pdf`} regCard={d.type === "registration_card"} testId={`stay-doc-download-${d.type}`} />
            </div>
          </div>
        );
      })}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="bg-white border border-[#D5E6E8] md:max-w-[900px] max-h-[92vh] overflow-hidden p-0" data-hocp-dialog data-testid="document-preview">
          <DialogHeader className="px-5 pt-4"><DialogTitle className="font-heading text-[15px]">{preview ? `${labelFor(preview)} · ${preview.number}` : ""}</DialogTitle><DialogDescription className="sr-only">Document preview</DialogDescription></DialogHeader>
          {html ? <iframe title="document" srcDoc={html} className="w-full h-[78vh] border-0 bg-[#F4FCFC]" data-testid="document-preview-frame" />
            : <div className="h-[40vh] flex items-center justify-center text-[13px] text-hocp-textMuted"><Loader2 size={16} className="animate-spin mr-2" />Rendering…</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
